// NOTICE: KAPTION-INTEGRATION
// conversation-tabs.TABS.1-5, OPEN.1-4, KEYS.1-3
//
// The tab list, and nothing else. A tab is a conversation id plus the little
// that has to survive a restart; everything shown on it — name, avatar, unread
// — is read from the store at render time, because the store is already the
// truth and a copy would only go stale.
//
// State lives at module scope so the strip, the conversation card and the
// shell's keyboard commands all move the same list, whichever component
// happens to be mounted.
import { computed, ref } from 'vue';

const STORAGE_KEY = 'kaption_conversation_tabs_v1';

/** conversation-tabs.TABS.2 — ten is enough to be useful and few enough to read. */
export const MAX_TABS = 10;

// { id: Number }
const tabs = ref([]);
const activeId = ref(null);
// conversation-tabs.TABS.3 — closed tabs, most recent last.
const closedStack = ref([]);
let accountScope = null;

const storageKeyFor = accountId => `${STORAGE_KEY}:${accountId}`;

const readStored = accountId => {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(accountId));
    const value = JSON.parse(raw || '{}');
    const list = Array.isArray(value.tabs) ? value.tabs : [];
    return {
      tabs: list
        .filter(tab => Number.isFinite(Number(tab?.id)))
        .slice(0, MAX_TABS)
        .map(tab => ({ id: Number(tab.id) })),
      activeId: Number.isFinite(Number(value.activeId))
        ? Number(value.activeId)
        : null,
    };
  } catch (error) {
    return { tabs: [], activeId: null };
  }
};

const persist = () => {
  if (accountScope === null) return;
  try {
    window.localStorage.setItem(
      storageKeyFor(accountScope),
      JSON.stringify({ tabs: tabs.value, activeId: activeId.value })
    );
  } catch (error) {
    // A full or blocked store costs the restore, not the session.
  }
};

const indexOf = id => tabs.value.findIndex(tab => tab.id === Number(id));

/**
 * conversation-tabs.TABS.5 — restores one account's tabs. Called with the
 * account id when the strip mounts; switching accounts re-scopes the list
 * rather than mixing two accounts' conversations into one strip.
 */
export const restoreTabs = accountId => {
  if (accountScope === Number(accountId)) return;
  accountScope = Number(accountId);
  const stored = readStored(accountScope);
  tabs.value = stored.tabs;
  activeId.value = stored.activeId;
  closedStack.value = [];
};

/** conversation-tabs.TABS.5 — a conversation that no longer resolves is dropped. */
export const pruneTabs = resolves => {
  const kept = tabs.value.filter(tab => resolves(tab.id));
  if (kept.length === tabs.value.length) return;
  tabs.value = kept;
  if (!kept.some(tab => tab.id === activeId.value)) {
    activeId.value = kept.length ? kept[kept.length - 1].id : null;
  }
  persist();
};

const evictForRoom = keepId => {
  if (tabs.value.length < MAX_TABS) return;
  // conversation-tabs.TABS.2 — the oldest tab goes.
  const victim = tabs.value.findIndex(tab => tab.id !== keepId);
  if (victim >= 0) tabs.value.splice(victim, 1);
};

/**
 * conversation-tabs.TABS.1, OPEN.1-2 — opens a conversation, or moves to it if
 * it is already open. `background` leaves the active tab where it is.
 */
export const openTab = (conversationId, { background = false } = {}) => {
  const id = Number(conversationId);
  if (!Number.isFinite(id)) return;
  if (indexOf(id) < 0) {
    evictForRoom(id);
    tabs.value.push({ id });
  }
  if (!background) activeId.value = id;
  persist();
};

/** conversation-tabs.TABS.3 — closing moves to a neighbour and remembers the tab. */
export const closeTab = conversationId => {
  const id = Number(conversationId);
  const at = indexOf(id);
  if (at < 0) return null;
  const [gone] = tabs.value.splice(at, 1);
  closedStack.value.push(gone);
  if (activeId.value !== id) {
    persist();
    return activeId.value;
  }
  const neighbour = tabs.value[at] || tabs.value[at - 1] || null;
  activeId.value = neighbour ? neighbour.id : null;
  persist();
  return activeId.value;
};

/** conversation-tabs.TABS.3 — reopen in the order they were closed. */
export const reopenClosedTab = () => {
  const back = closedStack.value.pop();
  if (!back) return null;
  evictForRoom(back.id);
  tabs.value.push(back);
  activeId.value = back.id;
  persist();
  return back.id;
};

export const activateTab = conversationId => {
  const id = Number(conversationId);
  if (indexOf(id) < 0) return null;
  activeId.value = id;
  persist();
  return id;
};

/** conversation-tabs.KEYS.1 — go to the nth tab, 0-based. */
export const selectTabAt = index => {
  const tab = tabs.value[index];
  if (!tab) return null;
  activeId.value = tab.id;
  persist();
  return tab.id;
};

/** conversation-tabs.KEYS.1 — previous or next, wrapping at both ends. */
export const cycleTab = step => {
  if (!tabs.value.length) return null;
  const at = indexOf(activeId.value);
  const next =
    tabs.value[(at + step + tabs.value.length) % tabs.value.length] ||
    tabs.value[0];
  activeId.value = next.id;
  persist();
  return next.id;
};

/** conversation-tabs.TABS.4 — drops the dragged tab at `to`. */
export const moveTab = (conversationId, to) => {
  const from = indexOf(conversationId);
  if (from < 0) return;
  const target = Math.max(0, Math.min(tabs.value.length - 1, to));
  const [moved] = tabs.value.splice(from, 1);
  tabs.value.splice(target, 0, moved);
  persist();
};

export function useKaptionConversationTabs() {
  return {
    tabs: computed(() => tabs.value),
    activeId: computed(() => activeId.value),
    hasClosedTabs: computed(() => closedStack.value.length > 0),
    restoreTabs,
    pruneTabs,
    openTab,
    closeTab,
    reopenClosedTab,
    activateTab,
    selectTabAt,
    cycleTab,
    moveTab,
  };
}

// Tests start from a known list; nothing in the app calls this.
export const resetTabsForTesting = () => {
  tabs.value = [];
  activeId.value = null;
  closedStack.value = [];
  accountScope = null;
};
